import { readonly, Ref, ref } from "@nuxtjs/composition-api";
import { ComposableFunctionArgs } from "~/composables/types";
import { Logger } from "~/helpers/logger";
import {
  UseOtpLoginResults,
  UseOtpLoginErrors,
  UseOtpLoginParams,
  UseOtpLoginInterface,
} from "~/modules/customer/composables/useOtpLogin/useOtpLogin";
import api from "~/config/extensionsApi";
import { postApiAxios } from "~/helpers";
import { useUser } from "~/modules/customer/composables/useUser";
import CryptoJS from "crypto-js";

export function useOtpLogin(): UseOtpLoginInterface {
  const loading: Ref<boolean> = ref(false);
  const isVerifyCheckout = ref(false);
  const result: Ref<UseOtpLoginResults> = ref({
    otpLoginResult: false,
    otpVerifyCaptchaResult: false,
    otpConfirmResult: {
      isOtpResult: false,
      isOtpConfirm: false,
    },
  });
  const error: Ref<UseOtpLoginErrors> = ref({
    otpLogin: null,
    otpConfirm: null,
    otpVerifyCaptcha: null,
    otpCheckout: null,
  });

  const { setCustomerToken } = useUser();

  const resetState = () => {
    result.value = {
      otpLoginResult: false,
      otpVerifyCaptchaResult: false,
      otpConfirmResult: {
        isOtpResult: false,
        isOtpConfirm: false,
      },
    };
    error.value = {
      otpLogin: null,
      otpConfirm: null,
      otpVerifyCaptcha: null,
      otpCheckout: null,
    };
  };

  const otpLogin = async (params: ComposableFunctionArgs<UseOtpLoginParams>) => {
    Logger.debug("useOtpLogin/otpLogin", params.mobileNumber);

    resetState();

    if (params.mobileNumber === "") {
      error.value.otpLogin = new Error("Vui lòng nhập số điện thoại!");
      return;
    }

    let key = CryptoJS.enc.Hex.parse(api.otp.crypto_key);
    let iv = CryptoJS.enc.Hex.parse(api.otp.crypto_iv);
    let encrypted = CryptoJS.AES.encrypt(params.mobileNumber, key, { iv: iv });
    encrypted = encrypted.ciphertext.toString(CryptoJS.enc.Base64);

    try {
      loading.value = true;
      // if (params.mobileNumber == '1111111111'){
      //   result.value.otpLoginResult = true;
      //   return;
      // }
      const response = await postApiAxios(api.otp.get_otp_login_v2, {
        mobile_number: encrypted,
        visitor_id: params.visitorId,
      });

      const json = response;

      if (json.result?.message === "visitor_id_not_valid") {
        error.value.otpLogin = new Error("visitor_id_not_valid");
        return;
      }

      if (json.code !== 200 || (json.result.errors && json.result.message !== "visitor_id_not_valid")) {
        error.value.otpLogin = new Error(json.result.message);
      } else {
        result.value.otpLoginResult = true;
      }
    } catch (err) {
      Logger.error("useOtpLogin/otpLogin", err);
      error.value.otpLogin = new Error("Unexpected authorization error. Check your Network connection.");
    } finally {
      loading.value = false;
    }
  };

  const loginOtpZalo = async (params: ComposableFunctionArgs<UseOtpLoginParams>) => {
    Logger.debug("useOtpLogin/loginOtpZalo", params.mobileNumber);

    resetState();

    if (params.mobileNumber === "") {
      error.value.otpLogin = new Error("Vui lòng nhập số điện thoại!");
      return;
    }
    let key = CryptoJS.enc.Hex.parse(api.otp.crypto_key);
    let iv = CryptoJS.enc.Hex.parse(api.otp.crypto_iv);
    let encrypted = CryptoJS.AES.encrypt(params.mobileNumber, key, { iv: iv });
    encrypted = encrypted.ciphertext.toString(CryptoJS.enc.Base64);

    try {
      loading.value = true;
      const response = await postApiAxios(api.otp.get_zalo_otp_login, {
        mobile_number: encrypted,
        visitor_id: params.visitorId,
      });

      const json = response;

      if (json.result?.message === "visitor_id_not_valid") {
        error.value.otpLogin = new Error("visitor_id_not_valid");
        return;
      }

      if (json.code !== 200 || (json.result.errors && json.result.message !== "visitor_id_not_valid")) {
        error.value.otpLogin = new Error(json.result.message);
      } else {
        result.value.otpLoginResult = true;
      }
    } catch (err) {
      Logger.error("useOtpLogin/loginOtpZalo", err);
      error.value.otpLogin = new Error("Unexpected authorization error. Check your Network connection.");
    } finally {
      loading.value = false;
    }
  };

  const otpConfirm = async (params: { mobileNumber: string; otp: string }) => {
    resetState();

    if (!params.otp) {
      error.value.otpConfirm = new Error("Vui lòng nhập mã OTP!");
      return;
    }
    try {
      loading.value = true;
      const response = await postApiAxios(api.otp.post_otp, {
        mobile_number: params.mobileNumber,
        otpbymobile: params.otp,
      });
      const json = response;
      if (json.code !== 200 || json.result.errors) {
        error.value.otpConfirm = new Error(json.result.message);
      } else {
        const token = json.result.token;
        await setCustomerToken(token);
        result.value.otpConfirmResult.isOtpConfirm = true;
      }
    } catch (err) {
      Logger.error("useOtpLogin/otpConfirm", err);
      error.value.otpConfirm = new Error("Unexpected authorization error. Check your Network connection.");
    } finally {
      loading.value = false;
    }
  };

  const otpVerifyCaptcha = async (params: { mobileNumber: string; visitorId: string }) => {
    resetState();

    try {
      loading.value = true;
      const response = await postApiAxios(api.otp.get_verify_otp_captcha, {
        mobile_number: params.mobileNumber,
        visitor_id: params.visitorId,
      });
      const json = response;
      if (json.code !== 200 || json.result.errors) {
        error.value.otpVerifyCaptcha = new Error(json.result.message);
        result.value.otpVerifyCaptchaResult = false;
      } else {
        result.value.otpVerifyCaptchaResult = true;
      }
    } catch (err) {
      Logger.error("useOtpLogin/otpVerifyCaptcha", err);
      error.value.otpVerifyCaptcha = new Error("Unexpected authorization error. Check your Network connection.");
    } finally {
      loading.value = false;
    }
  };

  const otpCheckout = async (params: { mobileNumber: string; otp: string }) => {
    if (!params.otp) {
      error.value.otpCheckout = new Error("Vui lòng nhập mã OTP!");
      return;
    }

    try {
      loading.value = true;
      const response = await postApiAxios(api.otp.post_otp_checkout, {
        mobile_number: params.mobileNumber,
        otpbymobile: params.otp,
      });
      const json = response;
      if (json.code !== 200 || json.result.errors) {
        error.value.otpCheckout = new Error(json.result.message);
      } else {
        isVerifyCheckout.value = json.result.is_verify;
      }
    } catch (err) {
      Logger.error("useOtpLogin/otpConfirm", err);
      error.value.otpConfirm = new Error("Unexpected authorization error. Check your Network connection.");
    } finally {
      loading.value = false;
    }
  };

  return {
    otpLogin,
    loginOtpZalo,
    otpConfirm,
    otpVerifyCaptcha,
    otpCheckout,
    isVerifyCheckout,
    loading: readonly(loading),
    result: readonly(result),
    error: readonly(error),
  };
}

export default useOtpLogin;
